import { FC, useCallback, useEffect, useState } from 'react'
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RestoreIcon from '@mui/icons-material/Restore'
import { useSearchParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { TabelaAcao } from '@/components/common/TabelaAcao'
import {
  BannerExpurgoFisico,
  ChipStatusExpurgo,
  GuardaPrivacidade,
  mensagemErroPrivacidade,
  useAcessoPrivacidade,
} from '@/components/privacidade/comum'
import { contagemAprovacoes, situacaoAprovacao } from '@/components/privacidade/aprovacao'
import { ConfirmacaoPorCodigoDialog } from '@/components/privacidade/ConfirmacaoPorCodigoDialog'
import { ExpurgoDetalheDrawer } from '@/components/privacidade/ExpurgoDetalheDrawer'
import { NovoExpurgoDialog } from '@/components/privacidade/NovoExpurgoDialog'
import { privacidadeService } from '@/services/privacidadeService'
import {
  ConfiguracaoPrivacidade,
  Expurgo,
  FUNDAMENTO_EXPURGO_LABEL,
  ORIGEM_EXPURGO_LABEL,
  STATUS_EXPURGO,
  STATUS_EXPURGO_LABEL,
  StatusExpurgo,
} from '@/types/privacidade'
import { formatDateTime } from '@/utils/formatters'

const CODIGO_REAPLICAR = 'REAPLICAR'

const Conteudo: FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const acesso = useAcessoPrivacidade()
  const [params, setParams] = useSearchParams()
  const protocoloFiltro = params.get('solicitacao') ?? ''

  const [configuracao, setConfiguracao] = useState<ConfiguracaoPrivacidade | null>(null)
  const [erroConfig, setErroConfig] = useState(false)
  const [filtroStatus, setFiltroStatus] = useState<StatusExpurgo | ''>('')
  const [lista, setLista] = useState<Expurgo[] | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [aberto, setAberto] = useState<string | null>(null)
  const [novo, setNovo] = useState(false)
  const [reaplicando, setReaplicando] = useState<'confirmar' | 'enviando' | null>(null)

  useEffect(() => {
    privacidadeService
      .configuracao()
      .then((c) => setConfiguracao(c))
      .catch(() => setErroConfig(true))
  }, [])

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      setLista(
        await privacidadeService.expurgos({
          status: filtroStatus || undefined,
          solicitacao: protocoloFiltro || undefined,
        })
      )
    } catch (e) {
      setErro(mensagemErroPrivacidade(e, 'Falha ao carregar a fila de expurgo'))
      setLista(null)
    } finally {
      setCarregando(false)
    }
  }, [filtroStatus, protocoloFiltro])

  useEffect(() => {
    carregar()
  }, [carregar])

  const reaplicar = async () => {
    setReaplicando('enviando')
    try {
      const r = await privacidadeService.reaplicar()
      // Os três números vêm prontos do backend; com falha, o aviso não é de sucesso.
      enqueueSnackbar(
        r
          ? `Reaplicação concluída: ${r.verificados} verificado(s), ${r.reaplicados} reaplicado(s), ${r.falhas} falha(s)`
          : 'Reaplicação solicitada',
        { variant: r && r.falhas > 0 ? 'warning' : 'success' }
      )
      setReaplicando(null)
      await carregar()
    } catch (e) {
      enqueueSnackbar(mensagemErroPrivacidade(e, 'Falha ao reaplicar'), { variant: 'error' })
      setReaplicando(null)
    }
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Fila de expurgo
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Expurgo definitivo de arquivos já removidos: duas aprovações de pessoas distintas (1ª DPO, 2ª ADMIN),
            presas à prévia; retenção legal sempre prevalece.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          {acesso.admin && (
            <Button variant="outlined" startIcon={<RestoreIcon />} onClick={() => setReaplicando('confirmar')}>
              Reaplicar após restauração
            </Button>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNovo(true)}>
            Novo expurgo
          </Button>
        </Stack>
      </Stack>

      <BannerExpurgoFisico configuracao={configuracao} />
      {erroConfig && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Não foi possível ler a configuração do expurgo físico.
        </Typography>
      )}

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Situação"
          value={filtroStatus}
          onChange={(e) => setFiltroStatus(e.target.value as StatusExpurgo | '')}
          sx={{ minWidth: 260 }}
        >
          <MenuItem value="">Todas</MenuItem>
          {STATUS_EXPURGO.map((s) => (
            <MenuItem key={s} value={s}>
              {STATUS_EXPURGO_LABEL[s]}
            </MenuItem>
          ))}
        </TextField>
        {protocoloFiltro && (
          <Alert
            severity="info"
            sx={{ py: 0 }}
            action={
              <Button size="small" onClick={() => setParams({})}>
                Limpar
              </Button>
            }
          >
            Vinculados à solicitação {protocoloFiltro}
          </Alert>
        )}
      </Stack>

      {carregando && !lista ? (
        <EstadoDeLista estado="carregando" />
      ) : erro ? (
        <EstadoDeLista estado="erro" detalhe={erro} acao={{ label: 'Tentar de novo', onClick: carregar }} />
      ) : lista && lista.length === 0 ? (
        <EstadoDeLista
          estado="vazio"
          mensagem="Nenhum expurgo na fila"
          detalhe={filtroStatus || protocoloFiltro ? 'Nada neste filtro.' : 'Nenhum expurgo foi pedido.'}
        />
      ) : lista ? (
        <TabelaAcao<Expurgo>
          aria-label="Fila de expurgo"
          colunas={[
            { titulo: 'Código', render: (e) => <strong>{e.codigo}</strong> },
            { titulo: 'Origem', render: (e) => ORIGEM_EXPURGO_LABEL[e.origem] ?? e.origem },
            {
              titulo: 'Fundamento',
              render: (e) =>
                `${FUNDAMENTO_EXPURGO_LABEL[e.fundamentoTipo] ?? e.fundamentoTipo}${e.solicitacaoProtocolo ? ` · ${e.solicitacaoProtocolo}` : ''}`,
            },
            {
              titulo: 'Situação',
              render: (e) => (
                <ChipStatusExpurgo
                  status={e.status}
                  expurgoFisicoHabilitado={configuracao?.expurgoFisicoHabilitado}
                  aguardandoHabilitacao={e.aguardandoHabilitacao}
                />
              ),
            },
            { titulo: 'Aprovações', render: (e) => `${contagemAprovacoes(e)}/2` },
            { titulo: 'Arquivos', render: (e) => e.itens?.length ?? e.totalItens ?? '—' },
            { titulo: 'Pedido em', render: (e) => formatDateTime(e.solicitadoEm) },
          ]}
          linhas={lista}
          chaveDaLinha={(e) => e.id}
          onAbrir={(e) => setAberto(e.id)}
          renderAcao={(e) => (situacaoAprovacao(e, acesso).tipo === 'pode' ? 'Aprovar →' : 'Abrir →')}
          rotuloAcessivelAcao={(e) => `Abrir ${e.codigo}`}
        />
      ) : null}

      <ExpurgoDetalheDrawer
        expurgoId={aberto}
        configuracao={configuracao}
        onFechar={() => setAberto(null)}
        onAlterado={carregar}
      />
      <NovoExpurgoDialog
        aberto={novo}
        protocoloInicial={protocoloFiltro || undefined}
        onFechar={() => setNovo(false)}
        onCriado={(e) => {
          setNovo(false)
          carregar()
          setAberto(e.id)
        }}
      />
      <ConfirmacaoPorCodigoDialog
        aberto={!!reaplicando}
        codigo={CODIGO_REAPLICAR}
        titulo="Reaplicar expurgos após restauração"
        descricao="Para cada expurgo executado, apaga de novo os documentos que voltaram de um backup restaurado. Idempotente; registra REAPLICADO na trilha."
        rotuloConfirmar="Reaplicar"
        corConfirmar="warning"
        processando={reaplicando === 'enviando'}
        onCancelar={() => setReaplicando(null)}
        onConfirmar={reaplicar}
      />
    </Box>
  )
}

/** Fila de expurgo (AN-12 §5, §12): ADMIN e DPO. */
export const FilaExpurgoPage: FC = () => (
  <GuardaPrivacidade>
    <Conteudo />
  </GuardaPrivacidade>
)

export default FilaExpurgoPage
